import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle, Download, FileText, ArrowRight, Loader2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ReportSuccess() {
  const { language } = useLanguage();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      checkStatus();
    } else {
      setLoading(false);
    }
  }, [sessionId]);

  const checkStatus = async () => {
    try {
      const response = await fetch(`${API}/reports/purchase/status/${sessionId}`);
      if (response.ok) {
        const data = await response.json();
        setStatus(data);
      }
    } catch (error) {
      console.error('Error checking status:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">
            {language === 'fr' ? 'Vérification du paiement...' : 'Verifying payment...'}
          </p>
        </div>
      </div>
    );
  }

  const isPaid = status?.payment_status === 'paid';

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="max-w-lg w-full" data-testid="report-success">
        <CardContent className="pt-8 pb-6 text-center">
          {isPaid ? (
            <>
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h1 className="text-2xl font-bold mb-2">
                {language === 'fr' ? 'Paiement Réussi!' : 'Payment Successful!'}
              </h1>
              <p className="text-muted-foreground mb-6">
                {language === 'fr' 
                  ? 'Votre accès aux rapports est maintenant actif. Vous recevrez un email de confirmation.'
                  : 'Your report access is now active. You will receive a confirmation email.'}
              </p>

              <div className="bg-muted/50 rounded-lg p-4 mb-6 text-left">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-muted-foreground">{language === 'fr' ? 'Montant' : 'Amount'}</span>
                  <span className="font-medium">${status?.amount} {status?.currency?.toUpperCase()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{language === 'fr' ? 'Statut' : 'Status'}</span>
                  <span className="text-green-600 font-medium capitalize">{status?.payment_status}</span>
                </div>
              </div>

              <div className="space-y-3">
                <Link to="/admin/reports/custom" className="block">
                  <Button className="w-full">
                    <FileText className="mr-2 h-4 w-4" />
                    {language === 'fr' ? 'Créer un Rapport Personnalisé' : 'Create Custom Report'}
                  </Button>
                </Link>
                <Link to="/analytics" className="block">
                  <Button variant="outline" className="w-full">
                    {language === 'fr' ? 'Voir les Analyses' : 'View Analytics'}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </>
          ) : (
            <>
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="h-8 w-8 text-yellow-600" />
              </div>
              <h1 className="text-2xl font-bold mb-2">
                {language === 'fr' ? 'Paiement en Attente' : 'Payment Pending'}
              </h1>
              <p className="text-muted-foreground mb-6">
                {language === 'fr' 
                  ? 'Votre paiement est en cours de traitement. Veuillez patienter quelques instants.'
                  : 'Your payment is being processed. Please wait a moment.'}
              </p>

              <div className="space-y-3">
                <Button onClick={checkStatus} className="w-full">
                  {language === 'fr' ? 'Vérifier à Nouveau' : 'Check Again'}
                </Button>
                <Link to="/" className="block">
                  <Button variant="outline" className="w-full">
                    {language === 'fr' ? 'Retour à l\'Accueil' : 'Return Home'}
                  </Button>
                </Link>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
